import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Preview → Production Sync Panel
 * ---------------------------------
 * Per doctrine §10.1, preview and production are separate MongoDB instances.
 * This tool gives the CEO a one-click way to export daily_sales from the
 * preview DB for a chosen date range + division, then upload that CSV on
 * production via the existing Bulk Import flow. Upserts on
 * (division_id, date, outlet), so re-runs are idempotent.
 */
const PreviewToProdSyncPanel = () => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const weekAgoIso = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('me');
  const [dateFrom, setDateFrom] = useState(weekAgoIso);
  const [dateTo, setDateTo] = useState(todayIso);
  const [previewRows, setPreviewRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/api/divisions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const visible = (data || []).filter((d) => !d.hidden);
        setDivisions(visible);
        if (!visible.find((d) => d._id === divisionId) && visible.length) {
          setDivisionId(visible[0]._id);
        }
      } catch (e) {
        setError('Could not load divisions');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = async () => {
    setLoading(true);
    setError('');
    setPreviewRows(null);
    try {
      const token = localStorage.getItem('access_token');
      const url = `${API}/api/daily-sales/export?division_id=${encodeURIComponent(divisionId)}&date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
      });
      const lines = (res.data || '').split('\n').filter(Boolean);
      const rowCount = Math.max(0, lines.length - 1);
      setPreviewRows({ count: rowCount, sampleCsv: lines.slice(0, Math.min(6, lines.length)).join('\n'), fullCsv: res.data });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!previewRows?.fullCsv) return;
    const blob = new Blob([previewRows.fullCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${divisionId}_${dateFrom}_to_${dateTo}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="rounded-xl border bg-white p-5 space-y-4"
      style={{ borderColor: '#E5E1DC' }}
      data-testid="preview-to-prod-sync-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: '#2C2A26' }} data-testid="sync-panel-title">
            Preview → Production Sync
          </h3>
          <p className="text-xs mt-1" style={{ color: '#6B645B' }}>
            Preview and production use <strong>separate databases</strong> (per Doctrine §10.1). Export daily_sales from
            this environment, then upload on <span style={{ color: '#2A7B88' }}>scaleboard.in → Admin → Data Import</span>.
            Safe to re-run — imports upsert on (date, outlet).
          </p>
        </div>
        <span
          className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded"
          style={{ backgroundColor: '#FFF7E6', color: '#B7791F' }}
          data-testid="sync-env-badge"
        >
          {window.location.hostname.includes('scaleboard.in') ? 'PROD' : 'PREVIEW'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-medium block mb-1" style={{ color: '#6B645B' }}>Division</label>
          <select
            value={divisionId}
            onChange={(e) => {
              setDivisionId(e.target.value);
              setPreviewRows(null);
            }}
            className="w-full rounded border px-2 py-1.5 text-sm"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="sync-division-select"
          >
            {divisions.map((d) => (
              <option key={d._id} value={d._id}>
                {`${d.name} (${d._id})`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-medium block mb-1" style={{ color: '#6B645B' }}>From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPreviewRows(null);
            }}
            className="w-full rounded border px-2 py-1.5 text-sm"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="sync-date-from"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium block mb-1" style={{ color: '#6B645B' }}>To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPreviewRows(null);
            }}
            className="w-full rounded border px-2 py-1.5 text-sm"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="sync-date-to"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={preview}
          disabled={loading}
          className="px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
          style={{ backgroundColor: '#2A7B88', color: 'white' }}
          data-testid="sync-preview-btn"
        >
          {loading ? 'Loading…' : 'Preview rows'}
        </button>
        {previewRows && previewRows.count > 0 && (
          <button
            onClick={downloadCsv}
            className="px-3 py-1.5 rounded text-xs font-medium"
            style={{ backgroundColor: '#2D8B5E', color: 'white' }}
            data-testid="sync-download-btn"
          >
            Download {previewRows.count}-row CSV
          </button>
        )}
        {error && (
          <span className="text-xs" style={{ color: '#B91C1C' }} data-testid="sync-error">
            {error}
          </span>
        )}
      </div>

      {previewRows && (
        <div
          className="rounded border p-3 text-xs font-mono overflow-x-auto"
          style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}
          data-testid="sync-preview-output"
        >
          <div className="text-[11px] font-sans font-medium mb-2" style={{ color: '#6B645B' }}>
            {previewRows.count === 0
              ? 'No daily_sales rows in this range — nothing to sync.'
              : `Showing first ${Math.min(5, previewRows.count)} of ${previewRows.count} rows (header + sample):`}
          </div>
          <pre className="whitespace-pre-wrap break-all" style={{ color: '#2C2A26' }}>
            {previewRows.sampleCsv}
          </pre>
        </div>
      )}

      <div className="text-[11px] border-t pt-3" style={{ borderColor: '#E5E1DC', color: '#6B645B' }}>
        <strong>Next step:</strong> Go to <span style={{ color: '#2A7B88' }}>scaleboard.in → Admin → Data Import</span>,
        pick this division, upload the CSV. Rows are upserted — no duplicates.
      </div>
    </div>
  );
};

export default PreviewToProdSyncPanel;
